import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Phone, Mail, Calendar, MoreVertical, Clock, DollarSign, Building2, User } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

interface Lead {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  value?: number;
  status: string;
  lead_source: string;
  notes?: string;
  last_contact_date?: string;
  next_follow_up?: string;
  created_at: string;
  updated_at: string;
  tags?: string[];
}

interface LeadCardProps {
  lead: Lead;
  onEdit?: (lead: Lead) => void;
  onAddNote?: (leadId: string) => void;
  onScheduleFollowUp?: (leadId: string) => void;
  onStageChange?: (leadId: string, newStage: string) => void;
  onViewDetails?: (lead: Lead) => void;
}

const LeadCard: React.FC<LeadCardProps> = ({
  lead,
  onEdit,
  onAddNote,
  onScheduleFollowUp,
  onStageChange,
  onViewDetails
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTimeInStage = () => {
    return formatDistanceToNow(new Date(lead.updated_at), { addSuffix: true });
  };

  const getLeadSourceColor = (source: string) => {
    const colors: Record<string, string> = {
      website: 'bg-blue-100 text-blue-800',
      referral: 'bg-green-100 text-green-800',
      cold_call: 'bg-purple-100 text-purple-800',
      event: 'bg-yellow-100 text-yellow-800',
      social_media: 'bg-pink-100 text-pink-800',
      manual: 'bg-gray-100 text-gray-800'
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      <Card className="group hover:shadow-md transition-all duration-200 cursor-pointer border-l-4 border-l-primary/20 hover:border-l-primary">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h4 
                className="font-semibold text-sm truncate text-foreground hover:text-primary transition-colors"
                onClick={() => setShowDetails(true)}
              >
                {lead.name}
              </h4>
              {lead.company && (
                <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-1">
                  <Building2 className="h-3 w-3" />
                  {lead.company}
                </p>
              )}
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setShowDetails(true)}>
                  <User className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit?.(lead)}>
                  Edit Lead
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAddNote?.(lead.id)}>
                  Add Note
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onScheduleFollowUp?.(lead.id)}>
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule Follow-up
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Value and Source */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1 text-green-600">
              <DollarSign className="h-3 w-3" />
              <span className="text-sm font-medium">
                {formatCurrency(lead.value || 0)}
              </span>
            </div>
            <Badge 
              variant="secondary" 
              className={`text-xs ${getLeadSourceColor(lead.lead_source)}`}
            >
              {lead.lead_source.replace('_', ' ')}
            </Badge>
          </div>

          {/* Time in stage */}
          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
            <Clock className="h-3 w-3" />
            <span>Updated {getTimeInStage()}</span>
          </div>

          {/* Tags */}
          {lead.tags && lead.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {lead.tags.slice(0, 2).map((tag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {lead.tags.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{lead.tags.length - 2} more
                </Badge>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-1">
            {lead.phone && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Call">
                <Phone className="h-3 w-3" />
              </Button>
            )}
            {lead.email && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Email">
                <Mail className="h-3 w-3" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 w-7 p-0" 
              onClick={() => onScheduleFollowUp?.(lead.id)}
              title="Schedule"
            >
              <Calendar className="h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lead Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {lead.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Contact Information</label>
                <div className="mt-2 space-y-2">
                  {lead.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{lead.email}</span>
                    </div>
                  )}
                  {lead.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{lead.phone}</span>
                    </div>
                  )}
                  {lead.company && (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{lead.company}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground">Lead Details</label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Estimated Value:</span>
                    <span className="text-sm font-medium text-green-600">
                      {formatCurrency(lead.value || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Source:</span>
                    <Badge className={getLeadSourceColor(lead.lead_source)}>
                      {lead.lead_source.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Created:</span>
                    <span className="text-sm">
                      {format(new Date(lead.created_at), 'MMM dd, yyyy')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {lead.notes && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Notes</label>
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{lead.notes}</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={() => onEdit?.(lead)} variant="outline">
                Edit Lead
              </Button>
              <Button onClick={() => onAddNote?.(lead.id)} variant="outline">
                Add Note
              </Button>
              <Button onClick={() => onScheduleFollowUp?.(lead.id)}>
                Schedule Follow-up
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeadCard;